<template>
  <!-- «Память обо мне» — что Зоя помнит о человеке (api/memory.js, lib/memory.js).
       Решение владельца 26.10.2026: всё, что Зоя запоминает, видно и правится
       здесь; пустая вкладка объясняется репликой Зои у вкладки (memory_empty). -->
  <div class="px-4 pt-[80px] pb-28">
    <div v-if="loading" class="flex justify-center pt-10">
      <div class="w-5 h-5 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
    </div>
    <p v-else-if="error" class="text-13 text-red-600">{{ error }}</p>

    <template v-else>
      <!-- Заметки Зои о разговорах (zoya_users.chat_summary, lib/chatSummary.js):
           обновляются сами раз в 30 сообщений, человек видит их и может очистить -->
      <h2 class="text-18 font-bold text-ink mb-2">Заметки Зои из чата</h2>
      <p class="text-13 text-ink/60 mb-2">Зоя ведёт их сама по ходу общения и перечитывает перед ответом.</p>
      <div class="mem-card">
        <p v-if="notes" class="text-15 text-ink leading-snug whitespace-pre-line">{{ notes }}</p>
        <p v-else class="text-15 text-ink/60 leading-snug">Заметок пока нет. Появятся после нескольких разговоров.</p>
        <div v-if="notes" class="flex mt-3">
          <button type="button" class="mem-btn" :disabled="busy" @click="clearNotes">Очистить заметки</button>
        </div>
      </div>

      <!-- Выключатель -->
      <label class="flex items-center justify-between rounded-2xl px-4 mt-6 mb-5" style="min-height: 53px; background: #EDE5D8;">
        <span class="text-ink py-2" style="font-family: Inter, sans-serif; font-size: var(--fs-15); font-weight: 600; line-height: 1.3;">
          Зоя может запоминать из разговора
        </span>
        <div class="relative shrink-0 ml-3" @click.prevent="toggleFromChat">
          <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: fromChat ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
          <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform" :class="{ 'translate-x-5': fromChat }" />
        </div>
      </label>

      <!-- Из профиля -->
      <h2 class="text-18 font-bold text-ink mt-2 mb-2">Из профиля</h2>
      <p class="text-13 text-ink/60 mb-2">Это Зоя тоже учитывает. Меняется в профиле.</p>
      <div class="mem-card">
        <p class="text-15 text-ink leading-snug"><span class="text-ink/60">Исключения из рациона:</span> {{ profile.allergies || 'не указаны' }}</p>
        <p class="text-15 text-ink leading-snug mt-1.5"><span class="text-ink/60">Предпочтения:</span> {{ profile.food_preferences || 'не указаны' }}</p>
        <p class="text-15 text-ink leading-snug mt-1.5"><span class="text-ink/60">Рекомендации по питанию:</span> {{ profile.nutrition_notes || 'не указаны' }}</p>
      </div>
      <!-- Факты -->
      <div class="flex items-center justify-between mt-7 mb-2">
        <h2 class="text-18 font-bold text-ink">Что Зоя учитывает</h2>
        <span class="text-13 text-ink/60">{{ facts.length }} из {{ limit }}</span>
      </div>

      <div v-for="f in facts" :key="f.id" class="mem-card">
        <template v-if="editingId !== f.id">
          <div class="flex items-start gap-2">
            <span class="mem-kind">{{ KIND_LABELS[f.kind] || f.kind }}</span>
            <button type="button" class="ml-auto text-14 text-warm font-semibold py-1" @click="startEdit(f)">Изменить</button>
          </div>
          <p class="text-15 text-ink mt-2 leading-snug">{{ f.text }}</p>
          <p class="text-12 text-ink/60 mt-1.5">
            {{ f.source === 'user' ? (ty ? 'ты попросил запомнить' : 'вы попросили запомнить') : 'Зоя поняла из разговора' }}
            · {{ fmt(f.created_at) }}<template v-if="f.expires_at"> · до {{ fmt(f.expires_at) }}</template>
          </p>
        </template>
        <template v-else>
          <select v-model="editKind" class="mem-select">
            <option v-for="k in KINDS" :key="k" :value="k">{{ KIND_LABELS[k] }}</option>
          </select>
          <textarea v-model="editText" rows="2" maxlength="200" class="mem-input mt-2" />
          <div class="flex gap-2 mt-2">
            <button type="button" class="mem-btn mem-btn-main" :disabled="busy || editText.trim().length < 3" @click="saveEdit(f)">Сохранить</button>
            <button type="button" class="mem-btn" :disabled="busy" @click="editingId = null">Отмена</button>
            <button type="button" class="mem-btn ml-auto text-red-600" :disabled="busy" @click="remove(f)">Удалить</button>
          </div>
        </template>
      </div>

      <!-- Добавить -->
      <div v-if="!adding" class="mt-2">
        <button type="button" class="mem-btn mem-btn-main w-full" style="min-height: 53px; font-size: var(--fs-16);" :disabled="facts.length >= limit" @click="adding = true">
          + Добавить, что Зое учитывать
        </button>
      </div>
      <div v-else class="mem-card">
        <select v-model="newKind" class="mem-select">
          <option v-for="k in KINDS" :key="k" :value="k">{{ KIND_LABELS[k] }}</option>
        </select>
        <textarea v-model="newText" rows="2" maxlength="200" class="mem-input mt-2"
          :placeholder="ty ? 'Например: не ем после 20:00, тренируюсь по утрам' : 'Например: не ем после 20:00, тренируюсь по утрам'" />
        <div class="flex gap-2 mt-2">
          <button type="button" class="mem-btn mem-btn-main" :disabled="busy || newText.trim().length < 3" @click="add">Запомнить</button>
          <button type="button" class="mem-btn" :disabled="busy" @click="adding = false; newText = ''">Отмена</button>
        </div>
      </div>

    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '../../services/api.js'
import { useAuthStore } from '../../store/auth.js'
import guide from '../../services/guide.js'

const authStore = useAuthStore()
const ty = computed(() => authStore.user?.address_form !== 'вы')

const KINDS = ['preference', 'restriction', 'routine', 'health', 'goal', 'context']
const KIND_LABELS = {
  preference: 'предпочтение', restriction: 'ограничение', routine: 'режим дня',
  health: 'здоровье', goal: 'цель', context: 'обстоятельства',
}

const loading = ref(true)
const error = ref('')
const busy = ref(false)
const facts = ref([])
const limit = ref(50)
const fromChat = ref(true)
const profile = ref({})
const notes = ref(null)
const editingId = ref(null)
const editText = ref('')
const editKind = ref('context')
const adding = ref(false)
const newText = ref('')
const newKind = ref('context')

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''

function hintCheck() {
  // Реплика «пока ничего не запомнила» — только если пусто и в фактах, и в заметках
  if (!facts.value.length && !notes.value) guide.request('memory_empty', { anchor: '[data-guide="zoya-tab-memory"]', place: 'below' })
  else guide.hide('memory_empty')
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get('/memory')
    facts.value = data.facts || []
    limit.value = data.limit || 50
    fromChat.value = data.settings?.memory_from_chat !== false
    profile.value = data.profile || {}
    notes.value = data.notes || null
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось загрузить'
  } finally {
    loading.value = false
    hintCheck()
  }
}

async function toggleFromChat() {
  const next = !fromChat.value
  fromChat.value = next
  try { await api.put('/memory/settings', { memory_from_chat: next }) }
  catch { fromChat.value = !next }
}

function startEdit(f) {
  editingId.value = f.id
  editText.value = f.text
  editKind.value = f.kind
}

async function saveEdit(f) {
  busy.value = true
  try {
    const { data } = await api.patch(`/memory/${f.id}`, { text: editText.value.trim(), kind: editKind.value })
    const i = facts.value.findIndex(x => x.id === f.id)
    if (i !== -1) facts.value[i] = data.fact
    editingId.value = null
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось сохранить'
  } finally { busy.value = false }
}

async function remove(f) {
  busy.value = true
  try {
    await api.delete(`/memory/${f.id}`)
    facts.value = facts.value.filter(x => x.id !== f.id)
    editingId.value = null
    hintCheck()
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить'
  } finally { busy.value = false }
}

async function clearNotes() {
  busy.value = true
  try {
    await api.delete('/memory/notes')
    notes.value = null
    hintCheck()
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось очистить'
  } finally { busy.value = false }
}

async function add() {
  busy.value = true
  try {
    const { data } = await api.post('/memory', { text: newText.value.trim(), kind: newKind.value })
    facts.value.push(data.fact)
    newText.value = ''
    adding.value = false
    hintCheck()
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось сохранить'
  } finally { busy.value = false }
}

onMounted(load)
onUnmounted(() => guide.hide('memory_empty'))
defineExpose({ reload: load })
</script>

<style scoped>
.mem-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 12px 14px;
  margin-bottom: 10px;
  font-family: Inter, sans-serif;
}
.mem-kind {
  font-size: var(--fs-12);
  font-weight: 600;
  color: var(--ink-70);
  background: #EDE5D8;
  border-radius: 999px;
  padding: 2px 8px;
}
.mem-input {
  width: 100%;
  border-radius: 14px;
  background: #fff;
  padding: 12px 14px;
  font-family: Inter, sans-serif;
  font-size: var(--fs-16);
  color: var(--ink);
  resize: none;
}
.mem-select {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  padding: 10px 12px;
  min-height: 44px;
  font-family: Inter, sans-serif;
  font-size: var(--fs-15);
  color: var(--ink);
}
.mem-btn {
  min-height: 48px;
  font-family: Inter, sans-serif;
  font-size: var(--fs-15);
  font-weight: 600;
  color: var(--ink);
  background: #EDE5D8;
  border-radius: 14px;
  padding: 12px 16px;
}
.mem-btn:disabled { opacity: .45; }
.mem-btn-main { background: #41291A; color: var(--cream); }
</style>
